import React from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import TextField from '@mui/material/TextField';
import Divider from '@mui/material/Divider';
import Tooltip from '@mui/material/Tooltip';
import useMediaQuery from '@mui/material/useMediaQuery';
import theme from '../../../util/theme/theme';
import Schedule from '../../schedule';
import CalendarInput from '../../ui/calendar-input';
import CustomRecurrenceBuilder from './custom-recurrence-builder';
import { useToast } from '../../../providers/toaster-provider';

interface RecurringProvisionProps {
    recurrenceType: 'daily' | 'weekly' | 'fortnightly' | 'monthly' | 'quarterly' | 'half-yearly' | 'yearly' | 'custom';
    setRecurrenceType: (val: 'daily' | 'weekly' | 'fortnightly' | 'monthly' | 'quarterly' | 'half-yearly' | 'yearly' | 'custom') => void;
    recurrencePattern: string;
    setRecurrencePattern: (val: string) => void;
    occurrences: number;
    setOccurrences: (val: number) => void;
    startDate: string;
    setStartDate: (val: string) => void;
    endDate: string;
    setEndDate: (val: string) => void;
    endDateType: 'on' | 'after' | 'never';
    setEndDateType: (val: 'on' | 'after' | 'never') => void;
    errors?: {
        recurrenceType?: string;
        occurrences?: string;
        startDate?: string;
        endDate?: string;
    };
    onStartDateChange?: (val: string) => void;
    onEndDateChange?: (val: string) => void;
    hasLinkedTransactions?: boolean;
    minDate?: string;
}

const RecurringProvision: React.FC<RecurringProvisionProps> = ({
    recurrenceType,
    setRecurrenceType,
    recurrencePattern,
    setRecurrencePattern,
    occurrences,
    setOccurrences,
    startDate,
    setStartDate,
    endDate,
    setEndDate,
    endDateType,
    setEndDateType,
    errors,
    onStartDateChange,
    onEndDateChange,
    hasLinkedTransactions,
    minDate
}) => {
    const [occurrencesHelperText, setOccurrencesHelperText] = React.useState<string>('');
    const { showError } = useToast();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const generateSequenceDates = (start: string, type: 'quarterly' | 'half-yearly', count: number = 5): string[] => {
        if (!start) return [];
        const dates: string[] = [];
        const baseDate = new Date(start);
        const monthsToAdd = type === 'quarterly' ? 3 : 6;
        
        for (let i = 0; i < count; i++) {
            const date = new Date(baseDate);
            date.setMonth(date.getMonth() + (i * monthsToAdd));
            dates.push(date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }));
        }
        return dates;
    };

    const handleOccurrencesChange = (value: number) => {
        let baseDate = startDate ? new Date(startDate) : new Date();
        // Only set start date if it's not already set
        if (!startDate) {
            setStartDate(baseDate.toISOString().slice(0, 10));
        }
        if (!isNaN(value)) {
            setOccurrences(value);
            if (endDateType !== 'never') {
                setEndDateType('on');

                let end = new Date(baseDate);

                switch (recurrenceType) {
                    case 'daily':
                        end.setDate(baseDate.getDate() + value - 1);
                        break;
                    case 'weekly':
                        end.setDate(baseDate.getDate() + 7 * (value - 1));
                        break;
                    case 'fortnightly':
                        end.setDate(baseDate.getDate() + 14 * (value - 1));
                        break;
                    case 'monthly':
                        end.setMonth(baseDate.getMonth() + (value - 1));
                        break;
                    case 'quarterly':
                        end.setMonth(baseDate.getMonth() + 3 * (value - 1));
                        break;
                    case 'half-yearly':
                        end.setMonth(baseDate.getMonth() + 6 * (value - 1));
                        break;
                    case 'yearly':
                        end.setFullYear(baseDate.getFullYear() + (value - 1));
                        break;
                    case 'custom':
                        // For custom recurrence, don't auto-calculate end date
                        return;
                    default:
                        break;
                }
                // Format as yyyy-mm-dd
                const formatted = end.toISOString().slice(0, 10);
                setEndDate(formatted);
            }
        }
    };

    const handleEndDateChange = (newEndDate: string) => {
        // If has linked transactions, prevent end date from being before start date
        if (hasLinkedTransactions && startDate && newEndDate < startDate) {
            showError('End Date cannot be earlier than an occurrence already linked to a transaction.');
            return;
        }
        if (onEndDateChange) {
            onEndDateChange(newEndDate);
        }
        setEndDate(newEndDate);
        if (!startDate || !newEndDate || endDateType === 'never') return;

        const start = new Date(startDate);
        const end = new Date(newEndDate);

        let calculatedOccurrences = 1;
        switch (recurrenceType) {
            case 'daily': {
                const diff = Math.floor((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24));
                calculatedOccurrences = diff >= 0 ? diff + 1 : 1;
                break;
            }
            case 'weekly': {
                // Map day names to day numbers (0 = Sunday, 1 = Monday, etc.)
                const dayMap: { [key: string]: number } = {
                    'Sunday': 0,
                    'Monday': 1,
                    'Tuesday': 2,
                    'Wednesday': 3,
                    'Thursday': 4,
                    'Friday': 5,
                    'Saturday': 6
                };
                
                // Extract day name from pattern (e.g., "weekly-Monday" -> "Monday")
                const dayName = recurrencePattern.split('-')[1];
                const targetDay = dayMap[dayName];
                
                if (targetDay !== undefined) {
                    // Find the first occurrence of the target day on or after start date
                    let firstOccurrence = new Date(start);
                    const startDayOfWeek = firstOccurrence.getDay();
                    const daysUntilTarget = (targetDay - startDayOfWeek + 7) % 7;
                    
                    firstOccurrence.setDate(firstOccurrence.getDate() + daysUntilTarget);
                    
                    // If first occurrence is after end date, no occurrences
                    if (firstOccurrence > end) {
                        calculatedOccurrences = 0;
                    } else {
                        // Calculate number of weeks between first occurrence and end date
                        const diff = Math.floor((end.getTime() - firstOccurrence.getTime()) / (1000 * 60 * 60 * 24 * 7));
                        calculatedOccurrences = diff + 1;
                    }
                } else {
                    // Fallback if recurrence pattern is not set
                    const diff = Math.floor((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24 * 7));
                    calculatedOccurrences = diff >= 0 ? diff + 1 : 1;
                }
                break;
            }
            case 'fortnightly': {
                // Map day names to day numbers (0 = Sunday, 1 = Monday, etc.)
                const dayMap: { [key: string]: number } = {
                    'Sunday': 0,
                    'Monday': 1,
                    'Tuesday': 2,
                    'Wednesday': 3,
                    'Thursday': 4,
                    'Friday': 5,
                    'Saturday': 6
                };
                
                // Extract day name from pattern (e.g., "fortnightly-Monday" -> "Monday")
                const dayName = recurrencePattern.split('-')[1];
                const targetDay = dayMap[dayName];
                
                if (targetDay !== undefined) {
                    // Find the first occurrence of the target day on or after start date
                    let firstOccurrence = new Date(start);
                    const startDayOfWeek = firstOccurrence.getDay();
                    const daysUntilTarget = (targetDay - startDayOfWeek + 7) % 7;
                    
                    firstOccurrence.setDate(firstOccurrence.getDate() + daysUntilTarget);
                    
                    // If first occurrence is after end date, no occurrences
                    if (firstOccurrence > end) {
                        calculatedOccurrences = 0;
                    } else {
                        // Calculate number of fortnights (2 weeks) between first occurrence and end date
                        const diff = Math.floor((end.getTime() - firstOccurrence.getTime()) / (1000 * 60 * 60 * 24 * 14));
                        calculatedOccurrences = diff + 1;
                    }
                } else {
                    // Fallback if recurrence pattern is not set
                    const diff = Math.floor((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24 * 14));
                    calculatedOccurrences = diff >= 0 ? diff + 1 : 1;
                }
                break;
            }
            case 'monthly': {
                const months = (end.getFullYear() - start.getFullYear()) * 12 + (end.getMonth() - start.getMonth());
                calculatedOccurrences = months >= 0 ? months + 1 : 1;
                break;
            }
            case 'quarterly': {
                const startQuarter = Math.floor(start.getMonth() / 3);
                const endQuarter = Math.floor(end.getMonth() / 3);
                const quarters = (end.getFullYear() - start.getFullYear()) * 4 + (endQuarter - startQuarter);
                calculatedOccurrences = quarters >= 0 ? quarters + 1 : 1;
                break;
            }
            case 'half-yearly': {
                const startHalf = Math.floor(start.getMonth() / 6);
                const endHalf = Math.floor(end.getMonth() / 6);
                const halfYears = (end.getFullYear() - start.getFullYear()) * 2 + (endHalf - startHalf);
                calculatedOccurrences = halfYears >= 0 ? halfYears + 1 : 1;
                break;
            }
            case 'yearly': {
                const years = end.getFullYear() - start.getFullYear();
                calculatedOccurrences = years >= 0 ? years + 1 : 1;
                break;
            }
            case 'custom':
                // For custom recurrence, don't auto-calculate occurrences
                return;
            default:
                break;
        }
        setOccurrences(calculatedOccurrences);
        setEndDateType('on');
    };

    // Update recurrence pattern based on start date - only when pattern is empty
    React.useEffect(() => {
        if (!startDate || recurrencePattern) return; // Skip if pattern already exists

        const startDateObj = new Date(startDate);
        const dayOfWeek = startDateObj.getDay();
        const dayOfMonth = startDateObj.getDate();
        const month = startDateObj.toLocaleString('en-US', { month: 'long' });
        const monthIndex = startDateObj.getMonth();

        const dayMap = {
            0: 'Sunday', 1: 'Monday', 2: 'Tuesday', 3: 'Wednesday',
            4: 'Thursday', 5: 'Friday', 6: 'Saturday'
        };

        const getSuffix = (n: number): string => {
            if (n % 100 >= 11 && n % 100 <= 13) return 'th';
            const lastDigit = n % 10;
            return ['th', 'st', 'nd', 'rd'][lastDigit] || 'th';
        };

        const getQuarter = (monthIndex: number): string => {
            if (monthIndex < 3) return 'Q1';
            if (monthIndex < 6) return 'Q2';
            if (monthIndex < 9) return 'Q3';
            return 'Q4';
        };

        let newPattern = '';
        if (recurrenceType === 'weekly') {
            const dayName = dayMap[dayOfWeek as keyof typeof dayMap];
            newPattern = `weekly-${dayName}`;
        } else if (recurrenceType === 'fortnightly') {
            const dayName = dayMap[dayOfWeek as keyof typeof dayMap];
            newPattern = `fortnightly-${dayName}`;
        } else if (recurrenceType === 'monthly') {
            newPattern = `monthly-${dayOfMonth}`;
        } else if (recurrenceType === 'quarterly') {
            const quarter = getQuarter(monthIndex);
            newPattern = `quarterly-${quarter}-${dayOfMonth}${getSuffix(dayOfMonth)}`;
        } else if (recurrenceType === 'half-yearly') {
            const halfYear = monthIndex < 6 ? 'H1' : 'H2';
            newPattern = `half-yearly-${halfYear}-${dayOfMonth}${getSuffix(dayOfMonth)}`;
        } else if (recurrenceType === 'yearly') {
            newPattern = `yearly-${month}-${dayOfMonth}${getSuffix(dayOfMonth)}`;
        }
        
        if (newPattern) {
            setRecurrencePattern(newPattern);
        }
    }, [startDate, recurrenceType, recurrencePattern, setRecurrencePattern]);

    // Recalculate occurrences when recurrence pattern changes (for weekly and fortnightly)
    React.useEffect(() => {
        if ((recurrenceType === 'weekly' || recurrenceType === 'fortnightly') && startDate && endDate && endDateType !== 'never') {
            const start = new Date(startDate);
            const end = new Date(endDate);

            const dayMap: { [key: string]: number } = {
                'Sunday': 0,
                'Monday': 1,
                'Tuesday': 2,
                'Wednesday': 3,
                'Thursday': 4,
                'Friday': 5,
                'Saturday': 6
            };
            
            const dayName = recurrencePattern.split('-')[1];
            const targetDay = dayMap[dayName];
            
            if (targetDay !== undefined) {
                let firstOccurrence = new Date(start);
                const startDayOfWeek = firstOccurrence.getDay();
                const daysUntilTarget = (targetDay - startDayOfWeek + 7) % 7;
                firstOccurrence.setDate(firstOccurrence.getDate() + daysUntilTarget);
                
                let calculatedOccurrences = 1;
                if (firstOccurrence > end) {
                    calculatedOccurrences = 0;
                } else {
                    // Use 7 days for weekly, 14 for fortnightly
                    const daysInterval = recurrenceType === 'fortnightly' ? 14 : 7;
                    const diff = Math.floor((end.getTime() - firstOccurrence.getTime()) / (1000 * 60 * 60 * 24 * daysInterval));
                    calculatedOccurrences = diff + 1;
                }
                setOccurrences(calculatedOccurrences);
            }
        }
    }, [recurrencePattern, recurrenceType, startDate, endDate, endDateType, setOccurrences]);

    return (
        <>
            <Box mb={1} display="grid" gridTemplateColumns={isMobile ? '1fr' : '1fr 3fr'} gap={1}>
                <Box sx={isMobile
                    ? { borderBottom: '1px solid #e0e0e0', pb: 1, mb: 1 }
                    : { borderRight: '1px solid #e0e0e0', pr: 2 }
                }>
                    <RadioGroup
                        value={recurrenceType}
                        onChange={e => setRecurrenceType(e.target.value as 'daily' | 'weekly' | 'fortnightly' | 'monthly' | 'quarterly' | 'half-yearly' | 'yearly' | 'custom')}
                    >
                        <FormControlLabel
                            value="daily"
                            control={<Radio size='small' sx={{ color: 'var(--color-text-muted)', height: 25 }} />}
                            label={<Typography sx={{ fontSize: 13 }}>Daily</Typography>}
                        />
                        <FormControlLabel
                            value="weekly"
                            control={<Radio size='small' sx={{ color: 'var(--color-text-muted)', height: 25 }} />}
                            label={<Typography sx={{ fontSize: 13 }}>Weekly</Typography>}
                        />
                        <FormControlLabel
                            value="fortnightly"
                            control={<Radio size='small' sx={{ color: 'var(--color-text-muted)', height: 25 }} />}
                            label={<Typography sx={{ fontSize: 13 }}>Fortnightly</Typography>}
                        />
                        <FormControlLabel
                            value="monthly"
                            control={<Radio size='small' sx={{ color: 'var(--color-text-muted)', height: 25 }} />}
                            label={<Typography sx={{ fontSize: 13 }}>Monthly</Typography>}
                        />
                        <FormControlLabel
                            value="quarterly"
                            control={<Radio size='small' sx={{ color: 'var(--color-text-muted)', height: 25 }} />}
                            label={<Typography sx={{ fontSize: 13 }}>Quarterly</Typography>}
                        />
                        <FormControlLabel
                            value="half-yearly"
                            control={<Radio size='small' sx={{ color: 'var(--color-text-muted)', height: 25 }} />}
                            label={<Typography sx={{ fontSize: 13 }}>Half-Yearly</Typography>}
                        />
                        <FormControlLabel
                            value="yearly"
                            control={<Radio size='small' sx={{ color: 'var(--color-text-muted)', height: 25 }} />}
                            label={<Typography sx={{ fontSize: 13 }}>Yearly</Typography>}
                        />
                        <Tooltip 
                            title="This feature is not available for your subscription. Contact info@fineants.com to upgrade." 
                            arrow
                            componentsProps={{
                                tooltip: {
                                    sx: {
                                        backgroundColor: '#ffe69a',
                                        color: '#333333',
                                        fontSize: '14px',
                                        border: '1px solid #c88500',
                                        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)'
                                    }
                                }
                            }}
                        >
                            <FormControlLabel
                                value="custom"
                                disabled
                                control={<Radio size='small' sx={{ color: 'var(--color-text-muted)', height: 25 }} />}
                                label={<Typography sx={{ fontSize: 13, color: 'var(--color-text-muted)' }}>Complex</Typography>}
                            />
                        </Tooltip>
                    </RadioGroup>
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Box>
                        <CalendarInput
                            label="Start Date"
                            value={startDate}
                            onChange={(val) => {
                                if (onStartDateChange) {
                                    onStartDateChange(val);
                                }
                                setStartDate(val);
                                
                                // Update pattern based on new start date
                                if (val && recurrencePattern) {
                                    const newDate = new Date(val);
                                    const dayOfWeek = newDate.getDay();
                                    const dayOfMonth = newDate.getDate();
                                    const month = newDate.toLocaleString('en-US', { month: 'long' });
                                    const monthIndex = newDate.getMonth();

                                    const dayMap = {
                                        0: 'Sunday', 1: 'Monday', 2: 'Tuesday', 3: 'Wednesday',
                                        4: 'Thursday', 5: 'Friday', 6: 'Saturday'
                                    };

                                    const getSuffix = (n: number): string => {
                                        if (n % 100 >= 11 && n % 100 <= 13) return 'th';
                                        const lastDigit = n % 10;
                                        return ['th', 'st', 'nd', 'rd'][lastDigit] || 'th';
                                    };

                                    const getQuarter = (monthIndex: number): string => {
                                        if (monthIndex < 3) return 'Q1';
                                        if (monthIndex < 6) return 'Q2';
                                        if (monthIndex < 9) return 'Q3';
                                        return 'Q4';
                                    };

                                    let newPattern = '';
                                    if (recurrenceType === 'weekly') {
                                        const dayName = dayMap[dayOfWeek as keyof typeof dayMap];
                                        newPattern = `weekly-${dayName}`;
                                    } else if (recurrenceType === 'fortnightly') {
                                        const dayName = dayMap[dayOfWeek as keyof typeof dayMap];
                                        newPattern = `fortnightly-${dayName}`;
                                    } else if (recurrenceType === 'monthly') {
                                        newPattern = `monthly-${dayOfMonth}`;
                                    } else if (recurrenceType === 'quarterly') {
                                        const quarter = getQuarter(monthIndex);
                                        newPattern = `quarterly-${quarter}-${dayOfMonth}${getSuffix(dayOfMonth)}`;
                                    } else if (recurrenceType === 'half-yearly') {
                                        const halfYear = monthIndex < 6 ? 'H1' : 'H2';
                                        newPattern = `half-yearly-${halfYear}-${dayOfMonth}${getSuffix(dayOfMonth)}`;
                                    } else if (recurrenceType === 'yearly') {
                                        newPattern = `yearly-${month}-${dayOfMonth}${getSuffix(dayOfMonth)}`;
                                    }
                                    
                                    if (newPattern) {
                                        setRecurrencePattern(newPattern);
                                    }
                                }
                            }}
                            disabled={hasLinkedTransactions}
                            error={!!errors?.startDate}
                            helperText={errors?.startDate}
                            minDate={minDate}
                        />
                    </Box>
                    { recurrenceType !== 'daily' && recurrenceType !== 'custom' && recurrenceType !== 'quarterly' && recurrenceType !== 'half-yearly' &&
                        <Box>
                            <Schedule
                                cycle={recurrenceType.charAt(0).toUpperCase() + recurrenceType.slice(1) as 'Daily' | 'Weekly' | 'Fortnightly' | 'Monthly' | 'Quarterly' | 'Half-yearly' | 'Yearly'}
                                value={recurrencePattern}
                                onChange={val => {
                                    // Check if it's yearly and February 29
                                    if (val.startsWith('yearly-') && val.includes('February') && val.includes('29')) {
                                        setOccurrencesHelperText('In non-leap years, this recurrence will apply on February 28.');
                                    } else if(['29', '30', '31'].includes(val.split('-')?.[1])) {
                                        setOccurrencesHelperText('For months without this date, the recurrence will be applied on the last day of the month');
                                    } else {
                                        setOccurrencesHelperText('');
                                    }
                                    
                                    // Sync start date with the selected 'every' pattern
                                    if (startDate && val) {
                                        const baseDate = new Date(startDate);
                                        
                                        if (val.startsWith('weekly-') || val.startsWith('fortnightly-')) {
                                            const dayName = val.split('-')[1];
                                            const dayMap: { [key: string]: number } = {
                                                'Sunday': 0, 'Monday': 1, 'Tuesday': 2, 'Wednesday': 3,
                                                'Thursday': 4, 'Friday': 5, 'Saturday': 6
                                            };
                                            const targetDay = dayMap[dayName];
                                            if (targetDay !== undefined) {
                                                const currentDay = baseDate.getDay();
                                                // Unwrapped (targetDay - currentDay) goes negative
                                                // whenever the newly-picked day falls earlier in the
                                                // week than the current start date's day - e.g.
                                                // Monday -> Sunday computes -1, pushing startDate
                                                // backward instead of forward to the next occurrence
                                                // of that weekday. Every other day-of-week alignment
                                                // in this file already wraps with + 7) % 7; this one
                                                // didn't.
                                                const diff = (targetDay - currentDay + 7) % 7;
                                                baseDate.setDate(baseDate.getDate() + diff);
                                                setStartDate(baseDate.toISOString().slice(0, 10));
                                            }
                                        } else if (val.startsWith('monthly-')) {
                                            const date = parseInt(val.split('-')[1]);
                                            if (!isNaN(date)) {
                                                const month = baseDate.getMonth();
                                                const year = baseDate.getFullYear();
                                                const maxDaysInMonth = new Date(year, month + 1, 0).getDate();
                                                baseDate.setDate(Math.min(date, maxDaysInMonth));
                                                setStartDate(baseDate.toISOString().slice(0, 10));
                                            }
                                        } else if (val.startsWith('yearly-')) {
                                            const parts = val.replace('yearly-', '').split('-');
                                            if (parts.length === 2) {
                                                const date = parseInt(parts[1]);
                                                if (!isNaN(date)) {
                                                    const year = baseDate.getFullYear();
                                                    const months: { [key: string]: number } = {
                                                        'January': 0, 'February': 1, 'March': 2, 'April': 3, 'May': 4, 'June': 5,
                                                        'July': 6, 'August': 7, 'September': 8, 'October': 9, 'November': 10, 'December': 11
                                                    };
                                                    const month = months[parts[0]];
                                                    if (month !== undefined) {
                                                        const maxDaysInMonth = new Date(year, month + 1, 0).getDate();
                                                        baseDate.setMonth(month);
                                                        baseDate.setDate(Math.min(date, maxDaysInMonth));
                                                        setStartDate(baseDate.toISOString().slice(0, 10));
                                                    }
                                                }
                                            }
                                        }
                                    }
                                    
                                    setRecurrencePattern(val);
                                }}
                            />
                            { occurrencesHelperText && 
                                <Typography variant="caption" fontStyle="italic" color="var(--color-text-muted)">
                                    {occurrencesHelperText}
                                </Typography>
                            }
                        </Box>
                    }
                    { (recurrenceType === 'quarterly' || recurrenceType === 'half-yearly') && startDate &&
                        <Box sx={{ mb: 1.5, p: 1, border: '1px solid #bdbdbdff', borderRadius: 1, backgroundColor: 'var(--color-container-background)' }}>
                            <Box sx={{ display: 'flex', flexDirection: 'row', gap: 2, justifyContent: 'space-between' }}>
                                {generateSequenceDates(startDate, recurrenceType as 'quarterly' | 'half-yearly').map((date, idx) => (
                                    <Typography key={idx} sx={{ fontSize: 13, color: 'var(--color-default-text)' }}>
                                        {date}
                                    </Typography>
                                ))}
                            </Box>
                        </Box>
                    }
                    { (recurrenceType === 'quarterly' || recurrenceType === 'half-yearly') && !startDate &&
                        <Box sx={{ mb: 1.5, p: 1, border: '1px solid #bdbdbdff', borderRadius: 1, backgroundColor: 'var(--color-container-background)', minHeight: 20, display: 'flex', alignItems: 'center' }}>
                            <Typography sx={{ fontSize: 13, color: '#9e9e9e', fontStyle: 'italic', textAlign: 'center', width: '100%' }}>
                                Select a start date to view dates
                            </Typography>
                        </Box>
                    }
                    { recurrenceType === 'custom' &&
                        <CustomRecurrenceBuilder
                            recurrencePattern={recurrencePattern}
                            setRecurrencePattern={setRecurrencePattern}
                            startDate={startDate}
                        />
                    }
                    <Box>
                        <RadioGroup
                            row={!isMobile}
                            value={endDateType}
                            onChange={(e) => {
                                const newValue = e.target.value as 'on' | 'after' | 'never';
                                setEndDateType(newValue);
                            }}
                            defaultValue="on"
                            sx={{
                                display: 'flex',
                                flexDirection: isMobile ? 'column' : 'row',
                                justifyContent: isMobile ? 'flex-start' : 'space-between',
                                alignItems: isMobile ? 'flex-start' : 'center',
                                flexWrap: 'nowrap',
                                gap: isMobile ? 0 : undefined,
                                mb: endDateType === 'never' ? 0 : 1,
                            }}
                        >
                            <FormControlLabel
                                value="on"
                                control={<Radio size='small' />} 
                                label={<Typography sx={{ fontSize: 13, textWrap: 'nowrap' }}>End by</Typography>} 
                            />
                            <FormControlLabel 
                                value="after" 
                                control={<Radio size='small' />} 
                                label={<Typography sx={{ fontSize: 13, textWrap: 'nowrap' }}>End after</Typography>} 
                            />
                            <FormControlLabel 
                                value="never" 
                                control={<Radio size='small' />} 
                                label={<Typography sx={{ fontSize: 13, textWrap: 'nowrap' }}>No end date</Typography>} 
                            />
                        </RadioGroup>
                    </Box>
                    {endDateType !== 'never' && (
                        <CalendarInput
                            error={!!errors?.endDate}
                            helperText={errors?.endDate}
                            label="End Date"
                            value={endDate}
                            onChange={handleEndDateChange}
                        />
                    )}
                    {endDateType !== 'never' && (
                        <Box 
                            display="flex" 
                            alignItems="center" 
                            gap={1} 
                            sx={{ 
                                border: `1px solid ${errors?.occurrences ? '#F44336' : '#bdbdbdff'}`, 
                                borderRadius: 1, pl: 2 
                            }}
                        >
                            <Typography sx={{ fontSize: 13, color: 'var(--color-text-muted)' }}>Occurrences:</Typography>
                            <Box width="100%">
                                <TextField
                                    fullWidth
                                    size="small"
                                    type="number"
                                    defaultValue={1}
                                    sx={{ 
                                        background: 'var(--color-page-background)', 
                                        '& input[type=number]': { MozAppearance: 'textfield' }, 
                                        '& .MuiOutlinedInput-notchedOutline': { border: 'none' } 
                                    }}
                                    inputProps={{ min: 1 }}
                                    value={occurrences}
                                    onChange={e => handleOccurrencesChange(Number(e.target.value))}
                                />
                            </Box>
                        </Box>
                    )}
                    <Box>
                        {errors?.occurrences && <Typography sx={{ fontSize: 12, color: '#d1180aff', mt: 0.5, ml: 2 }}>{errors.occurrences}</Typography>}
                    </Box>
                </Box>
            </Box>
        </>
    );
};

export default RecurringProvision;